import { useInkind } from '@gik/api/inkinds/inkind';
import type { UseStreamSWROptions } from '@gik/api/stream/useStream';
import { auth } from '@gik/auth';
import { SignInFlowCalloutBlock } from '@gik/auth/components/SignInFlow/SignInFlowCallout';
import { SignInFlowContentCopyVariant } from '@gik/auth/components/SignInFlow/SignInFlowStartContent';
import { timeoutDefaultValue } from '@gik/core/constants';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import { useInkindCan } from '@gik/core/store/permissions';
import { useUserStore } from '@gik/core/store/UserStore';
import { useBemCN } from '@gik/core/utils/bemBlock';
import repeatElement from '@gik/core/utils/repeatElement';
import replacePath from '@gik/core/utils/replacePath';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { AddInkindUserUpdateButton } from '@gik/inkind-page/components/AddInkindUserUpdateButton/AddInkindUserUpdateButton';
import InkindUserUpdatesTimelineModal, {
  InkindUserUpdatesTimelineModalBlockName,
} from '@gik/inkind-page/components/InkindUserUpdatesTimelineModal/InkindUserUpdatesTimelineModal';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import type InkindUserUpdate from '@gik/inkind-page/models/InkindUserUpdate';
import { openAddEditUpdateModal } from '@gik/inkind-page/utils/modals';
import type { StreamPaginationType } from '@gik/stream/components/Posts/StreamPagination';
import { StreamPagination } from '@gik/stream/components/Posts/StreamPagination';
import type { UserUpdateActivity } from '@gik/stream/types/Stream';
import StreamClient from '@gik/stream/utils/StreamClient';
import { Button } from '@gik/ui/Button';
import type { ICarouselProps } from '@gik/ui/Carousel';
import { Carousel } from '@gik/ui/Carousel';
import { Heading } from '@gik/ui/Heading';
import { UI } from '@gik/ui/UIManager';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type Slider from 'react-slick';
import useSearchParam from 'react-use/lib/useSearchParam';
import { inkindPageTourSelectors } from '../InkindPageTour/InkindPageTour';
import InkindUserUpdateCard from '../InkindUserUpdateCard/InkindUserUpdateCard';

export interface IInkindUserUpdatesProps {
  inkindRouteId: string;
}

const blockName = 'inkind-user-updates';
const userUpdateDeepLinkParameter = 'userUpdate';

function InkindUserUpdatesComp({ inkindRouteId }: IInkindUserUpdatesProps) {
  const bem = useBemCN(blockName);
  const { t } = useTranslation();

  const userId = useUserStore(state => state.id);
  const { data: inkindPage } = useInkind(inkindRouteId);
  const isMounted = useIsMounted();
  const canEdit = useInkindCan('manage', inkindRouteId, inkindPage?.groupId) && isMounted();
  const isMdDown = useBreakpoint(Breakpoint.MD_DOWN);

  const [slideNumber, setSlideNumber] = React.useState<number>(0);
  const closeTimeline = React.useRef<Function>(null);

  const userUpdateParam = useSearchParam(userUpdateDeepLinkParameter);

  const [userUpdates, setUserUpdates] = React.useState<InkindUserUpdate[]>();
  const [shouldRerender, setShouldRerender] = React.useState<boolean>(false);

  const paginationRef = React.useRef<StreamPaginationType>();

  /**
   * Open timeline modal
   */
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userUpdate = urlParams.get('userUpdate');

    if (userUpdate) {
      const matchingUpdate = userUpdates?.find(u => u.id === userUpdate);
      if (matchingUpdate) {
        openTimelineModal(matchingUpdate);
      }
    }
    // eslint-disable-next-line
  }, [userUpdateParam, userUpdates]);

  /**
   * Close timeline modal
   */
  React.useEffect(() => {
    if (userUpdates?.length === 0) {
      closeTimeline.current?.();
    }
  }, [userUpdates]);

  function rerender() {
    setShouldRerender(true);
    setTimeout(() => {
      setShouldRerender(false);
    }, timeoutDefaultValue);
  }

  async function onEditSuccess(update: InkindUserUpdate) {
    carouselRef.current?.slickGoTo(0);
    paginationRef.current?.reloadItem(update.id);
  }

  async function onDeleteUpdate() {
    rerender();
  }

  async function openTimelineModal(selectedUpdate?: InkindUserUpdate) {
    await UI.dialog(
      ({ close }) => {
        closeTimeline.current = close;

        return (
          <InkindUserUpdatesTimelineModal
            onEditSuccess={onEditSuccess}
            onDeleteUpdate={onDeleteUpdate}
            canEdit={canEdit}
            selectedUpdate={selectedUpdate}
            inkindRouteId={inkindRouteId}
          />
        );
      },
      {
        title: t(translationKeys.InkindUserUpdatesTimelineModalTitle).toString(),
        closable: true,
        contentId: InkindUserUpdatesTimelineModalBlockName,
      }
    );

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(userUpdateDeepLinkParameter);
    replacePath(urlParams);
  }

  const isLoggedIn = !!userId;
  const showTwoCardsNonMobile = !isLoggedIn || canEdit || userUpdates?.length > 0;
  const minCards = isMdDown || showTwoCardsNonMobile ? 2 : 1;
  const maxCards = 5;

  const carouselRef = React.useRef<Slider>();

  function onClickSignInToViewUpdates() {
    auth.signin({
      callout: SignInFlowCalloutBlock.UPDATE_VIEW_ATTEMPT,
      copyVariant: SignInFlowContentCopyVariant.INKIND_PAGE,
      routeId: inkindPage?.routeId,
    });
  }

  const handleAddUpdate = React.useCallback(() => {
    openAddEditUpdateModal({
      inkindRouteId,
      onSuccess: () => {
        // paginationRef.current?.loadLatestItems();
        rerender();
      },
    });
  }, [inkindRouteId]);

  const handlePaginationDataChange = React.useCallback((items: UserUpdateActivity[]) => {
    setUserUpdates(items?.map(item => mapActivityToUserUpdate(item)));
  }, []);

  const sanitizeData = React.useCallback((items: UserUpdateActivity[]) => sanitizeUserUpdates(items, userId), [userId]);

  const useFetch = React.useCallback(
    function useFetch(paginationOptions: UseStreamSWROptions) {
      if (!userId) return Promise.resolve([] as UserUpdateActivity[]);
      return StreamClient.getUserUpdates(inkindRouteId, {
        ...paginationOptions,
        recentReactionsLimit: 0,
      });
    },
    [inkindRouteId, userId]
  );

  const paginationHeader = React.useCallback(
    ({ sanitizedData: data }) => {
      return (
        <div {...bem('title-wrapper')}>
          <Heading level={2} {...bem('title')}>
            {t(translationKeys.InkindUserUpdatesTimelineModalTitle).toString()}
          </Heading>
          {canEdit && data?.length > 0 && (
            <AddInkindUserUpdateButton id={inkindPageTourSelectors.addUpdate} onClick={handleAddUpdate} />
          )}
        </div>
      );
    },
    [bem, canEdit, handleAddUpdate, t]
  );

  return (
    <div
      {...bem(null, [
        {
          'first-slide': slideNumber === 0,
        },
      ])}
    >
      <div {...bem('cards-wrapper')}>
        {!shouldRerender && (
          <StreamPagination<UserUpdateActivity>
            key={userId}
            ref={paginationRef}
            hasNextButton={false}
            perPage={maxCards}
            onChange={handlePaginationDataChange}
            order="desc"
            sanitizeData={sanitizeData}
            fetch={useFetch}
            header={paginationHeader}
            initialFetch
          >
            {({ sanitizedData: updates, isLoading, loadLatestItems, endReached }) => {
              const carouselOptions: Partial<ICarouselProps> = {
                ...bem('cards'),
                slidesToShow: isMdDown ? 1.125 : showTwoCardsNonMobile ? 2 : 1,
                infinite: false,
                autoplay: false,
                slidesToScroll: 1,
                arrows: updates?.length > (isMdDown ? 1 : 2),
                dots: false,
                beforeChange: (_: never, nextSlide: number) => setSlideNumber(nextSlide),
                legacyIndicator: true,
              };

              return (
                <>
                  {canEdit && !isLoading && updates?.length === 0 && endReached && (
                    <div {...bem('no-updates-button-container')}>
                      <AddInkindUserUpdateButton onClick={handleAddUpdate} />
                    </div>
                  )}

                  {!isLoggedIn && inkindPage && (
                    <div {...bem('sign-in-button-container')}>
                      <Button variant={'secondary'} onClick={onClickSignInToViewUpdates}>
                        {t(translationKeys.InkindUserUpdatesSignIn)}
                      </Button>
                    </div>
                  )}

                  {isMounted() && (
                    <Carousel ref={carouselRef} {...carouselOptions}>
                      {updates?.map(update => {
                        const userUpdate = mapActivityToUserUpdate(update);
                        return (
                          <InkindUserUpdateCard
                            key={update.id}
                            update={userUpdate}
                            canEdit={canEdit}
                            openAddUpdateModal={() => handleAddUpdate(loadLatestItems)}
                            openTimelineModal={openTimelineModal}
                          />
                        );
                      })}

                      {updates?.length === maxCards && (
                        <InkindUserUpdateCard canEdit={canEdit}>
                          <div {...bem('see-all-updates')}>
                            <Button
                              fullWidth
                              noFocusStyling
                              variant={'default-link'}
                              onClick={() => openTimelineModal()}
                            >
                              See all updates
                            </Button>
                          </div>
                        </InkindUserUpdateCard>
                      )}

                      {(() =>
                        updates?.length < minCards
                          ? repeatElement(
                              minCards - updates?.length,
                              i => (
                                <InkindUserUpdateCard
                                  isPlaceholder
                                  showAddButton={canEdit && !isLoading && updates?.length === 1}
                                  addButtonIndex={updates?.length + i}
                                  isLoggedIn={isLoggedIn}
                                  canEdit={canEdit}
                                  hasUpdates={updates?.length > 0}
                                  openAddUpdateModal={() => handleAddUpdate(loadLatestItems)}
                                />
                              ),
                              false
                            )
                          : [])()}
                    </Carousel>
                  )}
                </>
              );
            }}
          </StreamPagination>
        )}
      </div>
    </div>
  );
}

export const InkindUserUpdates = withComponentErrorBoundary(InkindUserUpdatesComp);

export const mapActivityToUserUpdate = (activity: UserUpdateActivity): InkindUserUpdate => ({
  id: activity.id,
  title: activity.userUpdate?.title,
  description: activity.userUpdate?.description,
  media: Object.values(activity.userUpdateMedia || activity.media || {}),
  organiserId: activity.actor as string,
  organiserFullName: activity.userUpdateStatic?.organiserFullName,
  publishedDate: activity.userUpdate?.publishedDate,
});

export function sanitizeUserUpdates(items: UserUpdateActivity[], userId: string) {
  // FIXME: move this logic to the backend!
  if (!userId) {
    return []; //logged out user cannot see updates
  }
  return items?.filter(item => !item.userUpdateDeleted && !item.deletedTime && !!item.userUpdate);
}
